import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, User, Phone, MessageSquare, Dog, CreditCard, Edit3, Trash2, DollarSign, Plus, Car, MapPin } from 'lucide-react';

const AppointmentList = ({ appointments = [], selectedDate, onEditAppointment, onDeleteAppointment }) => {
  const navigate = useNavigate();
  
  // Filtrar agendamentos do dia selecionado
  const dayAppointments = appointments.filter(apt => {
    if (!apt.dataAgendamento) return false;
    let aptDateRaw;
    if (apt.dataAgendamento?.toDate) {
      aptDateRaw = apt.dataAgendamento.toDate();
    } else if (apt.dataAgendamento instanceof Date) {
      aptDateRaw = apt.dataAgendamento;
    } else {
      aptDateRaw = new Date(apt.dataAgendamento);
    }
    
    // Criar cópia para não modificar o objeto original
    const aptDate = new Date(aptDateRaw);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    aptDate.setHours(0, 0, 0, 0);
    
    const isSameDay = aptDate.getTime() === selected.getTime();
    return isSameDay;
  });

  // Ordenar por horário
  const sortedAppointments = dayAppointments.sort((a, b) => {
    let dateARaw;
    if (a.dataAgendamento?.toDate) {
      dateARaw = a.dataAgendamento.toDate();
    } else if (a.dataAgendamento instanceof Date) {
      dateARaw = a.dataAgendamento;
    } else {
      dateARaw = new Date(a.dataAgendamento);
    }
    
    let dateBRaw;
    if (b.dataAgendamento?.toDate) {
      dateBRaw = b.dataAgendamento.toDate();
    } else if (b.dataAgendamento instanceof Date) {
      dateBRaw = b.dataAgendamento;
    } else {
      dateBRaw = new Date(b.dataAgendamento);
    }
    
    // Criar cópias para não modificar os objetos originais
    const dateA = new Date(dateARaw);
    const dateB = new Date(dateBRaw);
    
    return dateA.getTime() - dateB.getTime();
  });

  const getServiceTypeLabel = (type) => {
    // Como agora é um campo livre, retorna o valor diretamente
    return type || 'Serviço não informado';
  };

  const getServiceColor = (type) => {
    // Cor padrão para todos os serviços, já que agora são campos livres
    return 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300';
  };

  // Função para extrair apenas o primeiro nome com apenas a primeira letra maiúscula
  const getFirstName = (fullName) => {
    if (!fullName) return '';
    const firstName = fullName.trim().split(' ')[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  };

  const handleAppointmentClick = (appointment) => {
    if (appointment.idPet) {
      navigate(`/pets/${appointment.idPet}`);
    }
  };

  if (!selectedDate) {
    return (
      <div className="bg-card-light dark:bg-card-dark rounded-lg border border-border-light dark:border-border-dark p-6">
        <div className="text-center text-muted-light dark:text-muted-dark">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium">Selecione uma data</p>
          <p className="text-sm">Escolha um dia no calendário para ver os agendamentos</p>
        </div>
      </div>
    );
  }

  const weekday = selectedDate.toLocaleDateString('pt-BR', {
    weekday: 'long'
  });
  
  const day = selectedDate.getDate().toString().padStart(2, '0');
  const month = selectedDate.toLocaleDateString('pt-BR', {
    month: 'long'
  });
  // Deixar apenas a primeira letra minúscula
  const monthLowercase = month.charAt(0).toLowerCase() + month.slice(1);
  const year = selectedDate.getFullYear();
  
  const dateFormatted = `${day}/${monthLowercase}/${year}`;
  // Capitalizar apenas o dia da semana, não o mês
  const formattedDate = `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${dateFormatted}`;

  const totalDia = sortedAppointments.reduce((acc, apt) => {
    const servico = parseFloat(apt.precoServico ?? apt.servicePrice ?? 0) || 0;
    const transporte = (apt.temTransporte || apt.hasTransport) && ((apt.precoTransporte ?? apt.transportPrice) > 0)
      ? (parseFloat(apt.precoTransporte ?? apt.transportPrice) || 0)
      : 0;
    return acc + servico + transporte;
  }, 0);

  return (
    <div className="bg-card-light dark:bg-card-dark rounded-lg border border-border-light dark:border-border-dark overflow-hidden min-w-0 max-w-full">
      <div className="p-4 border-b border-border-light dark:border-border-dark">
        <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
          <h3 className="text-lg font-semibold text-foreground-light dark:text-foreground-dark min-w-0 break-words">
            {formattedDate}
          </h3>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm text-muted-light dark:text-muted-dark min-w-0">
            <span>
              {sortedAppointments.length} agendamento{sortedAppointments.length !== 1 ? 's' : ''}
            </span>
            {sortedAppointments.length > 0 && (
              <>
                <span className="hidden sm:inline text-border-light dark:text-border-dark">•</span>
                <span className="flex items-center text-muted-light dark:text-muted-dark">
                  Total: <span className="flex items-center gap-px ml-1"><DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />{totalDia.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 min-w-0 overflow-x-hidden">
        {sortedAppointments.length === 0 ? (
          <div className="text-center text-muted-light dark:text-muted-dark py-8">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Nenhum agendamento</p>
            <p className="text-sm">Não há agendamentos para este dia</p>
          </div>
        ) : (
          <div className="space-y-3 min-w-0">
            {sortedAppointments.map((appointment, index) => {
              let aptDateObj;
              if (!appointment.dataAgendamento) {
                aptDateObj = null;
              } else if (appointment.dataAgendamento?.toDate) {
                aptDateObj = appointment.dataAgendamento.toDate();
              } else if (appointment.dataAgendamento instanceof Date) {
                aptDateObj = appointment.dataAgendamento;
              } else {
                aptDateObj = new Date(appointment.dataAgendamento);
              }
              const appointmentDateValid = aptDateObj && !isNaN(aptDateObj.getTime());
              const appointmentDatePlusOneHour = appointmentDateValid ? new Date(aptDateObj.getTime() + 60 * 60 * 1000) : null;
              const now = new Date();
              const isPast = appointmentDatePlusOneHour ? appointmentDatePlusOneHour < now : false;
              const shouldShowAgendado = appointment.status === 'agendado' && !isPast;
              const statusBadge = (!appointmentDateValid || (!shouldShowAgendado && appointment.status === 'agendado')) ? null : (
                <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                  appointment.status === 'agendado'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                    : appointment.status === 'concluido'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                }`}>
                  {appointment.status === 'agendado' ? 'Agendado' :
                   appointment.status === 'concluido' ? 'Concluído' : 'Em andamento'}
                </span>
              );

              return (
              <div
                key={appointment.id || index}
                className="bg-background-light dark:bg-background-dark rounded-lg p-3 sm:p-4 border border-border-light dark:border-border-dark hover:shadow-md transition-all duration-200 overflow-hidden min-w-0"
              >
                {/* Mobile: data/hora + badge + botões em uma linha */}
                <div className="flex sm:hidden justify-between items-center gap-2 mb-2 flex-shrink-0">
                  <div className="flex items-center space-x-1 text-primary-600 dark:text-primary-400 min-w-0">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span className="text-xs font-medium truncate">
                      {!appointmentDateValid ? '—' : `${aptDateObj.toLocaleDateString('pt-BR')} às ${aptDateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {appointment.agendamentoAvulso && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                        Avulso
                      </span>
                    )}
                    {statusBadge}
                    {onEditAppointment && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditAppointment(appointment);
                        }}
                        className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all duration-200"
                        title="Editar agendamento"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {onDeleteAppointment && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteAppointment(appointment);
                        }}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                        title="Excluir agendamento"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Desktop: layout original */}
                <div className="flex items-start justify-between">
                  <div
                    onClick={() => handleAppointmentClick(appointment)}
                    className={`flex flex-wrap items-start gap-3 sm:gap-5 flex-1 min-w-0 ${
                      appointment.idPet ? 'cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/10 rounded-lg p-2 -m-2' : 'cursor-default'
                    }`}
                  >
                    <div className="hidden sm:flex items-center space-x-2 sm:space-x-3">
                      <div className="flex items-center space-x-1 sm:space-x-2 text-primary-600 dark:text-primary-400">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-medium">
                          {!appointmentDateValid ? '—' : `${aptDateObj.toLocaleDateString('pt-BR')} às ${aptDateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
                        </span>
                      </div>
                      {appointment.nomePet && (
                        <div className="flex items-center space-x-1">
                          <Dog className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                          <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{appointment.nomePet}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex sm:hidden flex-wrap items-start gap-2">
                      {appointment.nomePet && (
                        <div className="flex items-center space-x-1">
                          <Dog className="w-3 h-3 text-muted-light dark:text-muted-dark" />
                          <span className="text-xs text-foreground-light dark:text-foreground-dark">{appointment.nomePet}</span>
                        </div>
                      )}
                    </div>

                    {(((appointment.precoServico ?? appointment.servicePrice ?? 0) > 0) || appointment.eRecorrente) && (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                        <div className="flex items-center space-x-1" title="Preço do serviço">
                          <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-success-600 dark:text-success-400" />
                          <span className="text-xs sm:text-sm font-semibold text-success-600 dark:text-success-400">
                            {(appointment.precoServico ?? appointment.servicePrice ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        {((appointment.temTransporte || appointment.hasTransport) && ((appointment.precoTransporte || appointment.transportPrice) > 0)) && (
                          <>
                            <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-foreground-light dark:text-foreground-dark" />
                            <div className="flex items-center" title="Preço do transporte">
                              <Car className="w-3 h-3 sm:w-4 sm:h-4 text-primary-600 dark:text-primary-400" />
                              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-primary-600 dark:text-primary-400 -ml-0.5" />
                              <span className="text-xs sm:text-sm font-semibold text-primary-600 dark:text-primary-400 ml-1">
                                {(appointment.precoTransporte || appointment.transportPrice || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {(appointment.formaPagamento || appointment.paymentMethod) && (
                      <div className="flex items-center space-x-1">
                        <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                          {(() => {
                            const formaPagamento = appointment.formaPagamento || appointment.paymentMethod;
                            return formaPagamento === 'pix' ? 'PIX' :
                                   formaPagamento === 'cartao' ? 'Cartão' :
                                   formaPagamento === 'dinheiro' ? 'Dinheiro' : formaPagamento;
                          })()}
                        </span>
                      </div>
                    )}

                    {(appointment.ownernome || appointment.ownerName) && (
                      <div className="flex items-center space-x-1">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{getFirstName(appointment.ownernome || appointment.ownerName)}</span>
                      </div>
                    )}

                    {appointment.telefone ? (
                      <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1 min-w-0">
                        <div className="flex items-center space-x-1 min-w-0">
                          <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark truncate">
                            {appointment.telefone}
                          </span>
                        </div>
                        <span className="text-sm sm:text-base font-semibold text-foreground-light dark:text-foreground-dark break-words min-w-0">
                          • {getServiceTypeLabel(appointment.tipoServico)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm sm:text-base font-semibold text-foreground-light dark:text-foreground-dark break-words min-w-0 block">
                        • {getServiceTypeLabel(appointment.tipoServico)}
                      </span>
                    )}

                    {(appointment.agendamentoAvulso && (appointment.raca || appointment.endereco)) || appointment.observacoes ? (
                      <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1 w-full min-w-0 order-last">
                        {appointment.agendamentoAvulso && appointment.raca && (
                          <div className="flex items-center space-x-1">
                            <Dog className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                            <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                              Raça: {appointment.raca}
                            </span>
                          </div>
                        )}
                        
                        {appointment.agendamentoAvulso && appointment.endereco && (
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                            <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                              {appointment.endereco}
                            </span>
                          </div>
                        )}
                        
                        {appointment.observacoes && (
                          <div className="flex items-start space-x-1 min-w-0">
                            <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark mt-0.5 flex-shrink-0" />
                            <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark leading-relaxed break-words min-w-0">
                              {appointment.observacoes}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div className="hidden sm:flex items-center space-x-1 sm:space-x-2">
                    {appointment.agendamentoAvulso && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                        Avulso
                      </span>
                    )}
                    {statusBadge}
                    {onEditAppointment && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditAppointment(appointment);
                        }}
                        className="p-1.5 sm:p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all duration-200"
                        title="Editar agendamento"
                      >
                        <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    )}
                    {onDeleteAppointment && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteAppointment(appointment);
                        }}
                        className="p-1.5 sm:p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                        title="Excluir agendamento"
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentList;
